import { FaChartLine, FaRocket, FaShieldAlt, FaTag } from "react-icons/fa";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getDocOnce } from "../utils/firestoreOnce";

const ICONS = [FaRocket, FaTag, FaShieldAlt, FaChartLine];

// Neon color per card: blue, orange, purple, green
const NEON = [
  { color: '#38BDF8', rgb: '56,189,248' },
  { color: '#FAA617', rgb: '250,166,23' },
  { color: '#A78BFA', rgb: '167,139,250' },
  { color: '#34D399', rgb: '52,211,153' },
];

const DEFAULT_CARDS = [
  { titleAr: "الابتكار",      titleEn: "Innovation",   descriptionAr: "تطوير حلول إبداعية تضمن تجربة أفضل للمستخدم وتحقيق نتائج مميزة.", descriptionEn: "Developing creative solutions that ensure a better user experience and outstanding results." },
  { titleAr: "أفضل الأسعار", titleEn: "Best Prices",  descriptionAr: "نقدم خدمات عالية الجودة بأسعار تنافسية تناسب جميع الفئات.",          descriptionEn: "We provide high–quality services at competitive prices suitable for everyone." },
  { titleAr: "الجودة",        titleEn: "Quality",      descriptionAr: "نحرص على تقديم أعلى معايير الجودة في كل جزء من الخدمة.",              descriptionEn: "We maintain the highest standards of quality in every aspect of our service." },
  { titleAr: "الإنجاز",      titleEn: "Achievement",  descriptionAr: "تنفيذ المهام في الوقت المحدد وتحقيق أهداف العملاء بكفاءة عالية.",      descriptionEn: "Completing tasks on time and achieving client goals with maximum efficiency." },
];

const Honecard = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const [cards, setCards] = useState(DEFAULT_CARDS);

  useEffect(() => {
    let cancelled = false;
    getDocOnce('homePage/content')
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists() && snap.data().features?.length === 4) {
          setCards(snap.data().features);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="container mx-auto px-4 py-9 my-5" dir={isAr ? "rtl" : "ltr"}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => {
          const Icon = ICONS[index];
          const neon = NEON[index % NEON.length];

          return (
            <div
              key={index}
              className="group relative rounded-2xl p-[1.5px] overflow-hidden transition-transform duration-400 hover:-translate-y-3"
              style={{
                boxShadow: `0 0 22px rgba(${neon.rgb},0.15), 0 0 50px rgba(${neon.rgb},0.05)`,
              }}
            >
              {/* Spinning neon border */}
              <div
                className="absolute inset-[-150%] animate-[spin_5s_linear_infinite] opacity-60 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: `conic-gradient(transparent 0deg, transparent 160deg, rgba(${neon.rgb},0.85) 220deg, transparent 280deg)`,
                }}
              />
              {/* Static inner edge glow */}
              <div
                className="absolute inset-0 rounded-2xl"
                style={{ boxShadow: `inset 0 0 14px rgba(${neon.rgb},0.12)` }}
              />

              {/* Card body */}
              <div
                className="relative h-full rounded-2xl py-12 px-6 flex flex-col items-center text-center z-10"
                style={{
                  background: 'rgba(11,17,32,0.85)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  boxShadow: `0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(${neon.rgb},0.1)`,
                }}
              >
                {/* Top neon accent line */}
                <div
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-[2px] rounded-full"
                  style={{
                    background: `linear-gradient(to right, transparent, ${neon.color}, transparent)`,
                    boxShadow: `0 0 8px ${neon.color}`,
                  }}
                />

                {/* Icon */}
                <div
                  className="w-20 h-20 flex items-center justify-center mb-6 rounded-2xl transition-transform duration-500 group-hover:scale-110"
                  style={{
                    background: `rgba(${neon.rgb},0.10)`,
                    border: `1.5px solid rgba(${neon.rgb},0.45)`,
                    boxShadow: `0 0 18px rgba(${neon.rgb},0.25), inset 0 0 12px rgba(${neon.rgb},0.12)`,
                  }}
                >
                  <Icon
                    size="2em"
                    style={{ color: neon.color, filter: `drop-shadow(0 0 10px ${neon.color})` }}
                  />
                </div>

                {/* Title */}
                <h3
                  className="font-bold text-xl md:text-2xl mb-4 text-white"
                  style={{ textShadow: `0 0 16px rgba(${neon.rgb},0.6)` }}
                >
                  {isAr ? card.titleAr : card.titleEn}
                </h3>

                {/* Description */}
                <p className="leading-relaxed text-sm md:text-base text-gray-400">
                  {isAr ? card.descriptionAr : card.descriptionEn}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Honecard;
